import type { ValidationErrors } from '../../../utils/validation';
import type { IDiagnosis } from '../../interfaces/diagnosis';
import type { IProcess } from '../../interfaces/process';
import type { IProcedureAddEdit } from '../procedures/ProcedureItemAddEdit';
import type { ISupervisionEdit } from '../supervisions/SupervisionItemEdit';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { ServiceCode } from '../../../../common/enums/serviceCode';
import { dermaCscV1DiagnosticReportValidateOneCustomControlData } from './DermaCsc/v1/DermaCscV1DiagnosticReportAddEdit';
import { dermaCscV1RequestValidateOneCustomControlData } from './DermaCsc/v1/DermaCscV1RequestAddEdit';
import { dermaSnelCscV1DiagnosticReportValidateOneCustomControlData } from './DermaSnelCsc/v1/DermaSnelCscV1DiagnosticReportAddEdit';
import { dermaSnelCscV1RequestValidateOneCustomControlData } from './DermaSnelCsc/v1/DermaSnelCscV1RequestAddEdit';
import { ggzctV1RequestValidateOneCustomControlData } from './GGZCt/v1/GgzctV1RequestAddEdit';
import { orthopticsV1ProcedureDiagnosticReportValidateOneCustomControlData } from './OrthO/OrthopticsV1DiagnosticReportAddEdit';
import { osasc2V2DiagnosticReportValidateOneCustomControlData } from './OSASC2/v2/Osasc2V2DiagnosticReportAddEdit';
import { osasqV1RequestValidateOneCustomControlData } from './OSASQ/v1/OsasqV1RequestAddEdit';
import { osasvV1RequestValidateOneCustomControlData } from './OSASV/v1/OsasvV1RequestAddEdit';
import { tfcV1DiagnosticReportValidateOneCustomControlData } from './TFC/v1/TfcV1DiagnosticReportAddEdit';
import { tfoV1SupervisionValidateOneCustomControlData } from './TFO/v1/TfoV1SupervisionEdit';
import { tocV1DiagnosticReportValidateOneCustomControlData } from './TOC/v1/TocV1DiagnosticReportAddEdit';
import { tooV1DiagnosticReportValidateOneCustomControlData } from './TOO/v1/TooV1DiagnosticReportAddEdit';
import { tooV1ProcedureDiagnosticReportValidateOneCustomControlData } from './TOO/v1/TooV1ProcedureDiagnosticReportAddEdit';

export function validateOneCustomControlData(
    version: number,
    customControlEntity: CustomControlEntity,
    serviceCode: ServiceCode,
    customControlData: any,
    name: string,
    errors: ValidationErrors,
    process: IProcess | undefined,
    procedure?: IProcedureAddEdit,
    supervision?: ISupervisionEdit,
    diagnosis?: IDiagnosis
) {
    if (!customControlData) {
        return errors;
    }

    if (serviceCode === ServiceCode.OSASV) {
        if (customControlEntity === CustomControlEntity.RequestAddEdit) {
            if (version === 1) {
                return osasvV1RequestValidateOneCustomControlData(name, customControlData, errors);
            }
        }
    } else if (serviceCode === ServiceCode.TOO) {
        if (customControlEntity === CustomControlEntity.ProcedureDiagnosticReportAddEdit) {
            if (version === 1) {
                if (!procedure) {
                    throw new Error(
                        'procedure missing in validateOneCustomControlData for ProcedureDiagnosticReportAddEdit TOO'
                    );
                }
                errors = tooV1ProcedureDiagnosticReportValidateOneCustomControlData(name, procedure, errors);
            }
        } else if (customControlEntity === CustomControlEntity.DiagnosticReportAddEdit) {
            if (version === 1) {
                return tooV1DiagnosticReportValidateOneCustomControlData(name, customControlData, errors, diagnosis);
            }
        }
    } else if (serviceCode === ServiceCode.TFC) {
        if (customControlEntity === CustomControlEntity.DiagnosticReportAddEdit) {
            if (version === 1) {
                return tfcV1DiagnosticReportValidateOneCustomControlData(name, customControlData, errors);
            }
        }
    } else if (serviceCode === ServiceCode.TOC) {
        if (customControlEntity === CustomControlEntity.DiagnosticReportAddEdit) {
            if (version === 1) {
                return tocV1DiagnosticReportValidateOneCustomControlData(name, customControlData, errors, diagnosis);
            }
        }
    } else if (serviceCode === ServiceCode.TFO) {
        if (customControlEntity === CustomControlEntity.SupervisionEdit) {
            if (version === 1) {
                if (!supervision) {
                    throw new Error('supervision missing in validateOneCustomControlData for SupervisionItemEdit TFO');
                }

                return tfoV1SupervisionValidateOneCustomControlData(name, supervision, errors);
            }
        }
    } else if (serviceCode === ServiceCode.GGZCt) {
        if (customControlEntity === CustomControlEntity.RequestAddEdit) {
            if (version === 1) {
                return ggzctV1RequestValidateOneCustomControlData(name, customControlData, errors);
            }
        }
    } else if (serviceCode === ServiceCode.DermaCsc) {
        if (customControlEntity === CustomControlEntity.RequestAddEdit) {
            if (version === 1) {
                return dermaCscV1RequestValidateOneCustomControlData(name, customControlData, errors);
            }
        } else if (customControlEntity === CustomControlEntity.DiagnosticReportAddEdit) {
            if (version === 1) {
                return dermaCscV1DiagnosticReportValidateOneCustomControlData(name, customControlData, errors);
            }
        }
    } else if (serviceCode === ServiceCode.DermaSnelCsc) {
        if (customControlEntity === CustomControlEntity.RequestAddEdit) {
            if (version === 1) {
                return dermaSnelCscV1RequestValidateOneCustomControlData(name, customControlData, errors);
            }
        } else if (customControlEntity === CustomControlEntity.DiagnosticReportAddEdit) {
            if (version === 1) {
                return dermaSnelCscV1DiagnosticReportValidateOneCustomControlData(name, customControlData, errors);
            }
        }
    } else if (serviceCode === ServiceCode.OSASC2) {
        if (customControlEntity === CustomControlEntity.DiagnosticReportAddEdit) {
            if (version === 2) {
                return osasc2V2DiagnosticReportValidateOneCustomControlData(name, customControlData, errors);
            }
        }
    } else if (serviceCode === ServiceCode.OrthO || serviceCode === ServiceCode.OrthOFol) {
        if (customControlEntity === CustomControlEntity.ProcedureDiagnosticReportAddEdit) {
            if (version === 1) {
                if (!procedure) {
                    throw new Error(
                        'procedure missing in validateOneCustomControlData for ProcedureDiagnosticReportAddEdit OrthO'
                    );
                }
                errors = orthopticsV1ProcedureDiagnosticReportValidateOneCustomControlData(name, procedure, errors);
            }
        }
    } else if (serviceCode === ServiceCode.OSASQ) {
        if (customControlEntity === CustomControlEntity.RequestAddEdit) {
            if (version === 1) {
                return osasqV1RequestValidateOneCustomControlData(name, customControlData, errors);
            }
        }
    }

    return errors;
}
